import React from 'react';
import { ClosingCTA, Container, Eyebrow, PageHero, Section, sectionHeadingClass } from '../components/ui/Primitives';
import { StepFlow } from '../components/StepFlow';
import { FaqSection } from '../components/FaqSection';
import { Seo } from '../components/seo/Seo';
import { images, processFlow } from '../data/site';
import { faqs, pageSeo } from '../data/seo';
import { breadcrumbSchema, faqSchema, legalServiceSchema, organizationSchema, webPageSchema } from '../utils/schema';
const modelPoints = ['AI helps organize and prepare legal work.', 'Structured workflows improve consistency.', 'Lawyers review and guide the legal service.', 'The goal is a clearer, more modern client experience.'];
const whyItMatters = [{
  title: 'Better structure',
  body: 'Every matter follows a defined path.'
}, {
  title: 'More clarity',
  body: 'You always know what is happening and why.'
}, {
  title: 'Improved efficiency',
  body: 'Less time spent on repetitive groundwork.'
}, {
  title: 'A modern experience',
  body: 'Legal work that feels current, not dated.'
}, {
  title: 'Professional oversight',
  body: 'A lawyer reviews and signs off.'
}];
const seo = pageSeo.howItWorks;
const crumbs = [{
  label: 'Home',
  path: '/'
}, {
  label: 'How It Works',
  path: '/how-it-works'
}];
export function HowItWorks() {
  return <>
      <Seo title={seo.title} description={seo.description} path={seo.path} keywords={seo.keywords} image={images.abstract} schemas={[organizationSchema, legalServiceSchema, webPageSchema({
      ...seo
    }), breadcrumbSchema(crumbs), faqSchema(faqs)]} data-id="element-290" data-mp="1dy5pg72qx75n" />

      <PageHero eyebrow="The model" title="How NexLegal Works" subtitle="A modern legal model built around technology, AI, and lawyer oversight." crumbs={crumbs} data-id="element-291" data-mp="v2fjy9vltsn0" />

      {/* The model */}
      <Section ariaLabel="AI-powered legal workflows" data-id="element-292" data-mp="2pg6qv1mjtyer">
        <div className="grid gap-10 md:grid-cols-12 md:gap-16" data-id="element-293" data-mp="38js4gz3rn4to">
          <div className="md:col-span-6" data-id="element-294" data-mp="38i7dfjgnu40k">
            <Eyebrow data-id="element-295" data-mp="3dbsb1aeccvz8">01 — The model</Eyebrow>
            <h2 className={`mt-6 md:mt-7 ${sectionHeadingClass}`} data-id="element-296" data-mp="3dcuyf8cgggae">
              AI-powered legal workflows.
            </h2>
            <ul className="mt-9 divide-y divide-line border-y border-line md:mt-10" data-id="element-297" data-mp="34i6v3nqfx8o8">
              {modelPoints.map(point => <li key={point} className="py-5 text-[16px] leading-relaxed text-graphite md:text-[16.5px]" data-id="element-298" data-mp="3s92i87jybfp5">
                  {point}
                </li>)}
            </ul>
          </div>

          <div className="md:col-span-6" data-id="element-299" data-mp="3n8y1cgf4siye">
            <div className="rounded-sm border border-line bg-white p-6 sm:p-8 md:p-10" data-id="element-300" data-mp="3n7mzim58xl9x">
              <p className="text-[12px] uppercase tracking-[0.18em] text-mist" data-id="element-301" data-mp="26tn2bixsc9qd">
                The NexLegal model
              </p>
              <div className="mt-7 space-y-4 md:mt-8" data-id="element-302" data-mp="2bv2pphnfqsbx">
                <div className="rounded-sm bg-sand px-5 py-5 sm:px-6" data-id="element-303" data-mp="249gzjekwqpbe">
                  <p className="font-display text-[17px] font-medium text-ink" data-id="element-304" data-mp="1zb5nqyb1y4qj">
                    Client matter
                  </p>
                  <p className="mt-1 text-[14px] text-mist" data-id="element-305" data-mp="2d9vl8f1sa6ff">
                    The issue, the facts, the goal
                  </p>
                </div>
                <div aria-hidden="true" className="mx-auto h-8 w-px bg-line" data-id="element-306" data-mp="28a0s04ujd4r7" />
                <div className="grid gap-4 sm:grid-cols-2" data-id="element-307" data-mp="1kmj1sdqx6eqc">
                  <div className="rounded-sm border border-line px-5 py-5 sm:px-6" data-id="element-308" data-mp="1glparbi6kjt8">
                    <p className="font-display text-[17px] font-medium text-ink" data-id="element-309" data-mp="1ucm3r3g0r30t">
                      AI &amp; workflow
                    </p>
                    <p className="mt-1 text-[14px] text-mist" data-id="element-310" data-mp="1pk195zpaglw8">
                      Organize, structure, prepare
                    </p>
                  </div>
                  <div className="rounded-sm border border-line px-5 py-5 sm:px-6" data-id="element-311" data-mp="wxghsz8tc9so">
                    <p className="font-display text-[17px] font-medium text-ink" data-id="element-312" data-mp="1ajxgiyb89ka5">
                      Lawyer
                    </p>
                    <p className="mt-1 text-[14px] text-mist" data-id="element-313" data-mp="1fja2zhy6spc7">
                      Review, judgment, advice
                    </p>
                  </div>
                </div>
                <div aria-hidden="true" className="mx-auto h-8 w-px bg-line" data-id="element-314" data-mp="1p8jrknttnpvk" />
                <div className="rounded-sm bg-ink px-5 py-5 text-paper sm:px-6" data-id="element-315" data-mp="1ta5c8vcll8gb">
                  <p className="font-display text-[17px] font-medium" data-id="element-316" data-mp="1tc9ivl1vslka">Legal outcome</p>
                  <p className="mt-1 text-[14px] text-paper/60" data-id="element-317" data-mp="1yb2u16tp40ha">
                    Clear, professional, delivered
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Process flow */}
      <section aria-labelledby="process-heading" className="border-y border-line bg-sand/60" data-id="element-318" data-mp="8vd5syw9ls0h">
        <Container className="py-20 md:py-28 lg:py-32" data-id="element-319" data-mp="dteqtpd941el">
          <Eyebrow data-id="element-320" data-mp="2r3o8kt8z6cd1">02 — Process</Eyebrow>
          <h2 id="process-heading" className={`mt-6 max-w-[16ch] md:mt-7 ${sectionHeadingClass}`} data-id="element-321" data-mp="2r4pjmxpzqyur">
            From first question to final output.
          </h2>
          <StepFlow steps={processFlow} layout="list" data-id="element-322" data-mp="2ibcjpbtiewz6" />
        </Container>
      </section>

      {/* Why this matters */}
      <Section ariaLabel="A modern legal experience" data-id="element-323" data-mp="2da6ptao453o2">
        <div className="grid gap-10 md:grid-cols-12 md:gap-16" data-id="element-324" data-mp="3a0z72bms91rf">
          <div className="md:col-span-5" data-id="element-325" data-mp="360yivz9vtc76">
            <Eyebrow data-id="element-326" data-mp="3616uzoy6kdhb">03 — Why this matters</Eyebrow>
            <h2 className={`mt-6 md:mt-7 ${sectionHeadingClass}`} data-id="element-327" data-mp="311c59oq55txu">
              A modern legal experience.
            </h2>
            <img src={images.abstract} alt="Abstract network of fine connected lines representing structured legal workflows" width={1200} height={900} loading="lazy" decoding="async" className="mt-10 hidden aspect-[4/3] w-full rounded-sm object-cover md:block" data-id="element-328" data-mp="2w1fyxitz1qf4" />
          </div>
          <ul className="md:col-span-7" data-id="element-329" data-mp="1r3ui58jgmsam">
            {whyItMatters.map(item => <li key={item.title} className="grid gap-1 border-b border-line py-6 first:border-t sm:grid-cols-12 sm:gap-8" data-id="element-330" data-mp="1d436hegf1nzs">
                <h3 className="font-display text-[19px] font-medium tracking-tight text-ink sm:col-span-5 sm:text-[20px]" data-id="element-331" data-mp="1i4qyo3gdsavl">
                  {item.title}
                </h3>
                <p className="text-[15.5px] leading-relaxed text-graphite sm:col-span-7" data-id="element-332" data-mp="186b4tspyynie">
                  {item.body}
                </p>
              </li>)}
          </ul>
        </div>
      </Section>

      {/* Trust */}
      <section aria-labelledby="trust-heading" className="border-t border-line" data-id="element-333" data-mp="kf5qjhzog3a7">
        <Container className="py-20 md:py-28 lg:py-32" data-id="element-334" data-mp="kh8oqpazh2q0">
          <div className="grid gap-6 md:grid-cols-12 md:gap-10" data-id="element-335" data-mp="pd7as68ua8n9">
            <div className="md:col-span-4" data-id="element-336" data-mp="5s727vw49ozp">
              <Eyebrow data-id="element-337" data-mp="5t8eu4ss6jjj">04 — Trust</Eyebrow>
              <h2 id="trust-heading" className={`mt-6 md:mt-7 ${sectionHeadingClass}`} data-id="element-338" data-mp="tdn5z16lpxf">
                Lawyer review and oversight.
              </h2>
            </div>
            <p className="font-serif text-[23px] font-light leading-[1.34] text-ink sm:text-[27px] md:col-span-8 md:text-[34px]" data-id="element-339" data-mp="frxxw0weya1d">
              NexLegal does not replace lawyers. It uses technology to support better
              legal service delivery.
            </p>
          </div>
        </Container>
      </section>

      <FaqSection data-id="element-340" data-mp="jqdrinn02gnr" />

      <ClosingCTA headline="Want to see the model in practice?" body="Tell us about the matter you have in mind and we will explain how NexLegal would approach it." data-id="element-341" data-mp="ygur01g2qwi0" />
    </>;
}